import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { Check, ChevronsUpDown, Plus, UserPlus } from "lucide-react"
import { useState } from "react"
import { useCustomers } from "../../api/customerRequestsApi"
import { CreateCustomerModal } from "./CreateCustomerModal"

interface CustomerPickerProps {
  customerId: string | null
  customerName: string
  onChange: (customerId: string, customerName: string) => void
}

/**
 * Select-or-create customer control backed by GET/POST /customers. Renders as a
 * combobox: search the existing list, or type a new name and create it inline
 * (the confirmed request is stored against the resulting customer_id).
 */
export function CustomerPicker({
  customerId,
  customerName,
  onChange,
}: CustomerPickerProps) {
  const { translate } = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [createModalName, setCreateModalName] = useState<string | null>(null)

  const { data: customers = [] } = useCustomers()

  const q = query.trim().toLowerCase()
  const filtered = q
    ? customers.filter((c) => c.name.toLowerCase().includes(q))
    : customers
  const canCreate =
    query.trim().length > 0 &&
    !customers.some((c) => c.name.toLowerCase() === q)

  const handleCreated = (created: { id: string; name: string }) => {
    onChange(created.id, created.name)
    setQuery("")
    setOpen(false)
    setCreateModalName(null)
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <Popover
          open={open}
          onOpenChange={(o) => {
            setOpen(o)
            if (!o) setQuery("")
          }}
        >
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="h-9 w-full justify-between border-grey4 bg-grey2 px-3 text-sm font-normal text-white hover:bg-grey2"
            >
              <span className={cn("truncate", !customerName && "text-grey5")}>
                {customerName || translate("MC_Q_CUSTOMER_PLACEHOLDER")}
              </span>
              <ChevronsUpDown size={14} className="ml-2 shrink-0 text-grey5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-(--radix-popover-trigger-width) p-0"
          >
            <Command shouldFilter={false}>
              <CommandInput
                placeholder={translate("MC_Q_CUSTOMER_SEARCH")}
                value={query}
                onValueChange={setQuery}
              />
              <CommandList>
                {filtered.length === 0 && !canCreate && (
                  <CommandEmpty>
                    {translate("MC_Q_CUSTOMER_EMPTY")}
                  </CommandEmpty>
                )}
                {canCreate && (
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => {
                        const name = query.trim()
                        setOpen(false)
                        setQuery("")
                        setCreateModalName(name)
                      }}
                    >
                      <Plus size={14} className="mr-2" />
                      {translate("MC_Q_CUSTOMER_CREATE", {
                        name: query.trim(),
                      })}
                    </CommandItem>
                  </CommandGroup>
                )}
                {filtered.length > 0 && (
                  <CommandGroup>
                    {filtered.map((c) => (
                      <CommandItem
                        key={c.id}
                        value={c.id}
                        onSelect={() => {
                          onChange(c.id, c.name)
                          setQuery("")
                          setOpen(false)
                        }}
                      >
                        <Check
                          size={14}
                          className={cn(
                            "mr-2",
                            customerId === c.id ? "opacity-100" : "opacity-0",
                          )}
                        />
                        {c.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <Button
          type="button"
          variant="outline"
          className="h-9 shrink-0 gap-1.5 border-grey4 bg-grey2 px-3 text-sm font-normal text-white hover:bg-grey1"
          onClick={() => {
            setOpen(false)
            setQuery("")
            setCreateModalName("")
          }}
        >
          <UserPlus size={14} />
          {translate("MC_Q_CUSTOMER_CREATE_NEW_BTN")}
        </Button>
      </div>

      <CreateCustomerModal
        open={createModalName !== null}
        onOpenChange={(o) => {
          if (!o) setCreateModalName(null)
        }}
        initialName={createModalName ?? ""}
        onCreated={handleCreated}
      />
    </>
  )
}
